import { DateTime } from "luxon";
import { AgGrid } from "@jsq/ds/core";
import { ClientFilter } from "../../../GridFilters/ClientFilter";
import { InvoiceNumberFilter } from "../../../GridFilters/InvoiceNumberFilter";
import { PayeeNameFilter } from "../../../GridFilters/PayeeNameFilter";
import Money from "../../../Money";
import { SupportingDocumentLink } from "../../../SupportingDocumentLink";
import { PayableRowItem } from "./types";

/**
 * List of the supported columns in the Payable table
 * (ordered from left to right)
 */
export enum SupportedColumns {
  Payee = "payee.name",
  Arena = "arena.name",
  Entity = "entity.name",
  Amount = "amount",
  InvoiceNumber = "invoiceNumber",
  CreatedDate = "createdAt",
  ModifiedDate = "modifiedAt",
  DueDate = "dueDate",
  SupportingDocument = "supportingDocuments",
}

/**
 * The mapper provides the corresponding column name displayed in the table
 */
export const columnNames = new Map<SupportedColumns, string>([
  [SupportedColumns.Payee, "Payee"],
  [SupportedColumns.Arena, "Client"],
  [SupportedColumns.Entity, "Entity"],
  [SupportedColumns.Amount, "Amount"],
  [SupportedColumns.InvoiceNumber, "Invoice number"],
  [SupportedColumns.CreatedDate, "Created date"],
  [SupportedColumns.ModifiedDate, "Modified date"],
  [SupportedColumns.DueDate, "Due date"],
  [SupportedColumns.SupportingDocument, "Supporting document"],
]);

/**
 * AG Grid Column definitions for the Payable Table
 * Updated to include filter components and enable sorting for specific columns
 */
export const columnDefinitions: AgGrid.ColDef[] = [
  {
    field: SupportedColumns.Payee,
    headerName: columnNames.get(SupportedColumns.Payee),
    filter: PayeeNameFilter,
    menuTabs: ["generalMenuTab", "columnsMenuTab"],
    pinned: "left",
    minWidth: 360,
    sortable: false,
    resizable: false,
  },
  {
    field: SupportedColumns.Arena,
    headerName: columnNames.get(SupportedColumns.Arena),
    filter: ClientFilter,
    menuTabs: ["generalMenuTab", "columnsMenuTab"],
    sortable: false,
    resizable: false,
  },
  {
    field: SupportedColumns.Entity,
    headerName: columnNames.get(SupportedColumns.Entity),
    sortable: false,
    resizable: false,
  },
  {
    field: SupportedColumns.Amount,
    headerName: columnNames.get(SupportedColumns.Amount),
    sortable: true,
    cellRenderer: Money,
    cellRendererParams: ({ data }: { data: PayableRowItem }) => ({
      money: data.amount,
      rounded: false,
    }),
    resizable: false,
  },
  {
    field: SupportedColumns.InvoiceNumber,
    headerName: columnNames.get(SupportedColumns.InvoiceNumber),
    filter: InvoiceNumberFilter,
    menuTabs: ["generalMenuTab", "columnsMenuTab"],
    sortable: false,
    resizable: false,
  },
  {
    field: SupportedColumns.CreatedDate,
    headerName: columnNames.get(SupportedColumns.CreatedDate),
    valueFormatter: ({ data }: { data: PayableRowItem }) => {
      return data.createdAt.toLocaleString(DateTime.DATE_MED);
    },
    sortable: true,
    resizable: false,
  },
  {
    field: SupportedColumns.ModifiedDate,
    headerName: columnNames.get(SupportedColumns.ModifiedDate),
    valueFormatter: ({ data }: { data: PayableRowItem }) => {
      return data.modifiedAt.toLocaleString(DateTime.DATE_MED);
    },
    sortable: true,
    resizable: false,
  },
  {
    field: SupportedColumns.DueDate,
    headerName: columnNames.get(SupportedColumns.DueDate),
    valueFormatter: ({ data }: { data: PayableRowItem }) => {
      return data.dueDate ? data.dueDate.toLocaleString(DateTime.DATE_MED) : "";
    },
    sortable: true,
    resizable: false,
  },
  {
    field: SupportedColumns.SupportingDocument,
    headerName: columnNames.get(SupportedColumns.SupportingDocument),
    cellRenderer: SupportingDocumentLink,
    cellRendererParams: ({ data }: { data: PayableRowItem }) => ({
      supportingDocuments: data.supportingDocuments,
    }),
    sortable: false,
    resizable: false,
  },
];
